/**
 * PublicHeader
 * ------------
 * The top bar of every /p/* public page, in two states picked from the stored
 * session:
 *
 *   guest      brand · Events                         · Log in · Sign up free
 *   signed in  brand · My activities · Events · My registrations (count) ·
 *              My payments
 *                                                    · notifications · avatar menu
 *
 * "Log in" carries ?next= so the visitor comes back to the page they were on.
 * The registrations badge reads the feed's athlete summary — the same cache
 * entry /feed uses, so moving between the two costs no extra request.
 *
 * Renders on the athlete palette (FEED_TOKENS) like /feed, so the two
 * surfaces read as one product. Styles: styles/publicHeader.css.
 */

import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import envConfig from '../../config/envConfig';
import FEED_TOKENS from '../../theme/feedTokens';
import { pageHref } from '../../utils/pageHref';
import { isOrganizerAdmin } from '../../utils/roles';
import { usePublicApp } from '../../context/PublicAppContext';
import { useAppBranding } from '../../hooks/useAppBranding';
import { useNotifications } from '../../context/NotificationContext';
import { feedUserFrom, useAthleteSummary } from '../../pages/feed/useFeedData';
import {
  FEED_PATH, FEED_PROFILE_PATH, FEED_NOTIFICATIONS_PATH, EVENTS_PATH, REGISTRATIONS_PATH, MOTION_BOARD_PATH,
  FEED_PAYMENTS_PATH,
} from '../../pages/feed/feedRoutes';
import useSignOut from '../feed/useSignOut';
import AlertDialog from '../common/AlertDialog';
import useSession from './useSession';
import BrandMark from '../brand/BrandMark';
import '../../styles/publicHeader.css';

// Notifications are hidden from athletes for now, as in the feed sidebar; the
// route itself redirects too (App.js). Flip to restore the bell.
const SHOW_NOTIFICATIONS = false;

const Svg = ({ children, size = 20, sw = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const BellIcon = () => <Svg size={22} sw={1.8}><path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z" /><path d="M10 20a2 2 0 0 0 4 0" /></Svg>;
const MenuIcon = () => <Svg size={22}><path d="M4 7h16M4 12h16M4 17h16" /></Svg>;
const CloseIcon = () => <Svg size={22}><path d="M6 6l12 12M18 6L6 18" /></Svg>;
const PersonIcon = () => <Svg size={18}><circle cx="12" cy="8" r="4" /><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" /></Svg>;
const GridIcon = () => <Svg size={18}><rect x="4" y="4" width="7" height="7" rx="1.5" /><rect x="13" y="4" width="7" height="7" rx="1.5" /><rect x="4" y="13" width="7" height="7" rx="1.5" /><rect x="13" y="13" width="7" height="7" rx="1.5" /></Svg>;
const LogoutIcon = () => <Svg size={18}><path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l5-5-5-5M15 12H4" /></Svg>;

const navClass = ({ isActive }) => `ph-link${isActive ? ' active' : ''}`;

/** Closes an open popover on Escape, on a click outside `ref`, and on navigation. */
function useDismiss(open, setOpen, ref) {
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname, setOpen]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, setOpen, ref]);
}

function AccountMenu({ user, organizer, onSignOut, signingOut }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuId = useId();
  useDismiss(open, setOpen, ref);

  return (
    <div className="ph-account" ref={ref}>
      <button
        type="button"
        className="ph-avatar"
        aria-label={`Account menu for ${user.name}`}
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((o) => !o)}
      >
        {user.initials}
      </button>
      {open && (
        <div className="ph-pop" id={menuId}>
          <div className="ph-pop-who">
            <b>{user.name}</b>
            {user.handle && <span>{user.handle}</span>}
          </div>
          <Link to={FEED_PROFILE_PATH} className="ph-pop-item"><PersonIcon />Profile</Link>
          {organizer && <Link to="/organizer" className="ph-pop-item"><GridIcon />Organizer dashboard</Link>}
          <hr />
          <button type="button" className="ph-pop-item" onClick={onSignOut} disabled={signingOut}>
            <LogoutIcon />{signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * `brandOnly` draws the bar with nothing but the logo and the app name — no
 * navigation, no Log in / Sign up free, no avatar menu, no burger or drawer.
 * The brand still links home. Pages that are a task in themselves (a
 * registration form, say) use it to keep the site's brand bar without
 * offering a way out of the task. .ph-bar is a flex row whose brand comes
 * first, so removing the rest needs no CSS of its own.
 *
 * The hooks below still run in this mode — they cannot be called
 * conditionally — but nothing reads their result. The athlete summary query
 * is disabled on a null userId, so it costs no request.
 */
export default function PublicHeader({ brandOnly = false }) {
  const userData = useSession();
  const signedIn = Boolean(userData);
  const user = feedUserFrom(userData);
  const { appBranding: pageBranding } = usePublicApp();
  const brandingQuery = useAppBranding();
  const { pathname, search } = useLocation();
  const { unreadCount = 0 } = useNotifications() || {};
  const { data: summary } = useAthleteSummary(signedIn ? user.userId : null);
  const { signOut, busy, error, clearError } = useSignOut();

  const [menuOpen, setMenuOpen] = useState(false);
  const barRef = useRef(null);
  const drawerId = useId();
  useDismiss(menuOpen, setMenuOpen, barRef);

  // The page's own branding arrives with its data; the app-wide query is
  // fetched once at start-up and cached, so after the first page it is there
  // immediately. Until one of them answers, the brand slot holds a neutral
  // placeholder — falling back to envConfig's name here would flash the wrong
  // name and mark on a slow network. The fallback is only for a failed fetch.
  const appBranding = pageBranding || brandingQuery.data || null;
  const brandingPending = !appBranding && brandingQuery.isPending;
  const appName = appBranding?.app_name || envConfig.appName || 'AthloHive';

  // An application may point "My registrations" at its own Page Builder page
  // (and call it "My tickets", "My orders"…); both halves are required.
  const memberLink = appBranding?.member_link;
  const registrations = memberLink?.page && memberLink?.label
    ? { to: pageHref(memberLink.page), label: memberLink.label }
    : { to: REGISTRATIONS_PATH, label: 'My registrations' };
  const regCount = summary?.registrations?.total || 0;

  const next = encodeURIComponent(pathname + search);

  // Results sit beside Events for everybody, signed in or not. The boards are
  // public, and before this the only way to reach one was to type its id.
  const links = signedIn ? (
    <>
      <NavLink to={FEED_PATH} end className={navClass}>My activities</NavLink>
      <NavLink to={EVENTS_PATH} className={navClass}>Events</NavLink>
      <NavLink to={MOTION_BOARD_PATH} className={navClass}>Results</NavLink>
      <NavLink to={registrations.to} className={navClass}>
        {registrations.label}
        {regCount > 0 && <span className="ph-count">{regCount > 99 ? '99+' : regCount}</span>}
      </NavLink>
      {/* No count badge: a payment count is not news the way an upcoming
          entry is, and a number beside money reads as an amount. */}
      <NavLink to={FEED_PAYMENTS_PATH} className={navClass}>My payments</NavLink>
    </>
  ) : (
    <>
      <NavLink to={EVENTS_PATH} className={navClass}>Events</NavLink>
      <NavLink to={MOTION_BOARD_PATH} className={navClass}>Results</NavLink>
    </>
  );

  return (
    <header className="ph-root" style={FEED_TOKENS} ref={barRef}>
      {/* Bootstrap's .container — the homepage header's width and gutters. */}
      <div className="container ph-bar">
        <Link to="/" className="ph-brand" aria-label={brandingPending ? 'Home' : `${appName} home`} aria-busy={brandingPending || undefined}>
          <BrandMark name={appName} logo={appBranding?.logo} pending={brandingPending} />
        </Link>

        {!brandOnly && <nav className="ph-nav" aria-label="Primary">{links}</nav>}

        {!brandOnly && (
        <div className="ph-actions">
          {signedIn ? (
            <>
              {SHOW_NOTIFICATIONS && (
                <Link
                  to={FEED_NOTIFICATIONS_PATH}
                  className="ph-iconbtn"
                  aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
                >
                  <BellIcon />
                  {unreadCount > 0 && <span className="ph-dot">{unreadCount > 9 ? '9+' : unreadCount}</span>}
                </Link>
              )}
              <AccountMenu user={user} organizer={isOrganizerAdmin(userData)} onSignOut={signOut} signingOut={busy} />
            </>
          ) : (
            <>
              {/* Both carry ?next=. Only "Log in" used to: a guest on an event
                  page who picked "Sign up free" — the likelier choice, since
                  they have no account — arrived at /sign-up with nothing to
                  come back to, and landed on /feed afterwards however they
                  signed up. The sign-up screen passes the value on from here
                  (withNext on its "already have an account" link, stashSsoNext
                  on its SSO buttons), so this one attribute is what connects a
                  new visitor's registration to the event they came from. */}
              <Link to={`/sign-in?next=${next}`} className="ph-btn ph-btn-line">Log in</Link>
              <Link to={`/sign-up?next=${next}`} className="ph-btn ph-btn-primary">Sign up free</Link>
            </>
          )}
          <button
            type="button"
            className="ph-iconbtn ph-burger"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls={drawerId}
            onClick={() => setMenuOpen((o) => !o)}
          >
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
        )}
      </div>

      {!brandOnly && menuOpen && (
        <nav className="ph-drawer" id={drawerId} aria-label="Primary">
          <div className="container ph-drawer-in">
            {links}
            {!signedIn && (
              <div className="ph-drawer-cta">
                <Link to={`/sign-in?next=${next}`} className="ph-btn ph-btn-line">Log in</Link>
                <Link to={`/sign-up?next=${next}`} className="ph-btn ph-btn-primary">Sign up free</Link>
              </div>
            )}
          </div>
        </nav>
      )}

      {!brandOnly && <AlertDialog open={!!error} title="Logout Failed" message={error || ''} type="error" okLabel="OK" onOk={clearError} />}
    </header>
  );
}
